import { Button, ButtonLevel, ButtonSize, Skeleton, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { AlertCircleIcon, Image01Icon } from '@vizit/icons';
import { type BenchmarkMosaicUrl, LibraryEmptyState } from '@vizit/molecules';
import { cva, cx } from '@vizit/theme';
import type * as React from 'react';
import type { BenchmarkAction } from '../benchmark-actions-menu';
import { BenchmarkCard } from '../benchmark-card';
import { loadingCountClasses } from '../collection-card-grid/styles';

export interface BenchmarkItem {
  /**
   * Unique identifier for the benchmark
   */
  id: string;
  /**
   * The benchmark name
   */
  name: string;
  /**
   * The benchmark description, offered when editing it
   */
  description?: string | null;
  /**
   * The product category name, or `null` when none is set yet
   */
  categoryName: string | null;
  /**
   * Number of images in the benchmark
   */
  imageCount: number;
  /**
   * Number of collections scored against the benchmark, offered when deleting it
   *
   * @optional
   */
  collectionCount?: number;
  /**
   * Up to four thumbnail URLs for the card mosaic, each optionally with a fallback
   */
  previewImageUrls: BenchmarkMosaicUrl[];
  /**
   * Display name of the user who created the benchmark
   */
  creatorName?: string;
}

export interface BenchmarkCardGridProps {
  /**
   * The benchmarks to display
   */
  benchmarks: BenchmarkItem[];
  /**
   * Whether the benchmarks are loading
   */
  isLoading?: boolean;
  /**
   * Whether the benchmarks failed to load; shows an error state instead of the grid
   */
  isError?: boolean;
  /**
   * Called from the error state's Try again button
   */
  onRetry?: () => void;
  /**
   * Number of placeholder cards while loading
   */
  loadingCount?: number;
  /**
   * Custom CSS class for the grid container
   */
  className?: string;
  /**
   * Action shown opposite the count, e.g. the Create benchmark button
   */
  headerAction?: React.ReactNode;
  /**
   * Content shown inside the empty state, e.g. the Create benchmark button. While the grid is empty the count
   * row and `headerAction` are hidden, as in the collection grid.
   */
  emptyStateContent?: React.ReactNode;
  /**
   * The page URL of a benchmark; makes each card a link to it
   */
  getBenchmarkHref?: (id: string) => string;
  /**
   * Called on a plain click of a card, to navigate without a page load
   */
  onBenchmarkNavigate?: (id: string) => void;
  /**
   * Called with the action chosen from a card's "⋯" menu; the menu is hidden without it
   */
  onBenchmarkAction?: (id: string, action: BenchmarkAction) => void;
  /**
   * Menu actions shown but not available yet
   */
  disabledBenchmarkActions?: BenchmarkAction[];
}

// Wider cards than the collection grid: the mosaic needs the width to read.
const gridClasses = cva({
  base: ['grid gap-8', 'grid-cols-1', 'md:grid-cols-2', 'xl:grid-cols-3', '3xl:grid-cols-4'],
});

/**
 * The Library's benchmark grid: a "Showing N benchmarks" header with an
 * action slot, then one BenchmarkCard per benchmark. With no benchmarks it
 * shows only the empty state, holding `emptyStateContent`.
 */
export function BenchmarkCardGrid(props: BenchmarkCardGridProps) {
  const {
    benchmarks,
    isLoading = false,
    isError = false,
    onRetry,
    loadingCount = 8,
    className,
    headerAction,
    emptyStateContent,
    getBenchmarkHref,
    onBenchmarkNavigate,
    onBenchmarkAction,
    disabledBenchmarkActions,
  } = props;

  const count = benchmarks.length;
  const isEmpty = !isLoading && !isError && count === 0;

  if (isEmpty) {
    return (
      <LibraryEmptyState
        heading="No benchmarks found"
        description="No benchmarks match your search, or there are no benchmarks yet."
        icon={Image01Icon}
        className={className}
      >
        {emptyStateContent}
      </LibraryEmptyState>
    );
  }

  return (
    <div className={cx('w-full', className)}>
      <div className="mb-6 flex w-full flex-wrap items-center justify-between gap-4">
        {isError ? null : isLoading ? (
          <Skeleton variant="square" className={loadingCountClasses()} />
        ) : (
          <Text size={TextSize.Sm} color={TextColor.Tertiary} weight={TextWeight.Medium} showEllipsis={false}>
            Showing {count} {count === 1 ? 'benchmark' : 'benchmarks'}
          </Text>
        )}
        {headerAction && <div className="ml-auto">{headerAction}</div>}
      </div>

      {isError ? (
        <LibraryEmptyState
          heading="Couldn't load benchmarks"
          description="Something went wrong while loading benchmarks. Try again in a moment."
          icon={AlertCircleIcon}
        >
          {onRetry && (
            <Button level={ButtonLevel.Secondary} size={ButtonSize.Sm} onPress={() => onRetry()}>
              Try again
            </Button>
          )}
        </LibraryEmptyState>
      ) : (
        <div
          role={isLoading ? 'status' : 'group'}
          aria-label={
            isLoading
              ? 'Loading benchmarks'
              : `Benchmark grid containing ${count} ${count === 1 ? 'benchmark' : 'benchmarks'}`
          }
          className={gridClasses()}
        >
          {isLoading
            ? Array.from({ length: loadingCount }, (_, index) => {
                const skeletonId = `benchmark-skeleton-${index}`;
                return <BenchmarkCard key={skeletonId} id={skeletonId} name="" imageCount={0} isLoading />;
              })
            : benchmarks.map((benchmark) => (
                <BenchmarkCard
                  key={benchmark.id}
                  id={benchmark.id}
                  name={benchmark.name}
                  categoryName={benchmark.categoryName}
                  imageCount={benchmark.imageCount}
                  previewImageUrls={benchmark.previewImageUrls}
                  creatorName={benchmark.creatorName}
                  href={getBenchmarkHref?.(benchmark.id)}
                  onNavigate={onBenchmarkNavigate}
                  onAction={onBenchmarkAction}
                  disabledActions={disabledBenchmarkActions}
                />
              ))}
        </div>
      )}
    </div>
  );
}
